import type { Item, Section } from './types';

export const UNCATEGORIZED = '__none';

/**
 * Group items by category. Ported from the spec's `buildSections()`:
 *
 *  - the uncategorized section comes **first** and carries no label;
 *  - other sections keep first-seen order and are labelled uppercase;
 *  - within every section, done items sort to the bottom, stably — two items
 *    with the same `done` keep their server order.
 *
 * Written as an explicit partition rather than a comparator over the key list:
 * a comparator that returns 0 for every non-special pair relies on sort
 * stability to preserve first-seen order, which is guaranteed by the spec but
 * easy to break the next time someone "tidies" the comparator.
 */
export function buildSections(items: Item[]): Section[] {
  const order: string[] = [];
  const byCategory = new Map<string, Item[]>();

  for (const item of items) {
    const key = item.category ?? UNCATEGORIZED;
    let bucket = byCategory.get(key);
    if (!bucket) {
      bucket = [];
      byCategory.set(key, bucket);
      order.push(key);
    }
    bucket.push(item);
  }

  const keys = [
    ...order.filter((k) => k === UNCATEGORIZED),
    ...order.filter((k) => k !== UNCATEGORIZED),
  ];

  return keys.map((key) => {
    const bucket = byCategory.get(key) ?? [];
    return {
      key,
      label: key === UNCATEGORIZED ? '' : key.toUpperCase(),
      // Array.prototype.sort is stable per spec, so the two partitions below
      // keep their relative order.
      items: [...bucket.filter((i) => !i.done), ...bucket.filter((i) => i.done)],
    };
  });
}

/**
 * Whether two items share the `(category, done)` bucket that the server's
 * `position` orders within — `service._bucket`, mirrored.
 *
 * Compared field by field rather than through a composed key, because a
 * category is arbitrary user text: any sentinel standing in for "no category"
 * is a string somebody can type. `null === null` needs no sentinel.
 *
 * Case-insensitive on the category for the same reason the server is. A list
 * converges on one spelling, but an optimistic write shows the typed one for a
 * frame, and an item must not appear to leave its own bucket in the meantime.
 */
function sameBucket(a: Item, b: Item): boolean {
  const key = (item: Item) => (item.category === null ? null : item.category.toLowerCase());
  return a.done === b.done && key(a) === key(b);
}

export type MoveDirection = 'up' | 'down';

/** What `POST /api/items/{id}/move` should be sent to step an item one place. */
export interface MoveStep {
  /** The item to land after, or `null` for the top of the section. */
  afterId: number | null;
}

/**
 * The move that steps `item` one place up or down, or `null` when there is no
 * such move — it is already at that end of its bucket.
 *
 * `null` means **hide the control**, not disable it. The server 400s a move
 * that would leave the bucket, so an "up" on the top row is a button that can
 * only ever error, and this codebase's rule for those is the viewer render's:
 * the affordance is absent rather than inert.
 *
 * **The neighbour on screen is not the answer.** The tempting version of this
 * reads the row above in the rendered list, and it is wrong with or without a
 * filter. A rendered section is not a bucket: `buildSections` sorts done items
 * to the bottom of each section, so the row above the first done one is an
 * undone one, and the server 400s a move across that line. Under a filter it is
 * worse — `Confirm the hotel` in the fixtures is *last* in the server's order
 * and *first* on screen, so "the row above" can be several places away in the
 * order that actually exists. Deriving the bucket here, from ids rather than
 * from positions on screen, is what makes the visible section the right unit.
 *
 * **Pass the unfiltered list anyway.** It happens that the To do / Done filter
 * cannot change a bucket's membership — it filters on `done`, which is half the
 * bucket key, so every bucket-mate of a visible item is visible too. That is a
 * coincidence between this feature and that one, not a property worth resting
 * on: a filter on category, or a search box narrowing the rows, would break it
 * silently, and the symptom would be the same tap meaning different things
 * depending on which tab was selected. The unfiltered array costs nothing and
 * keeps the request independent of what is on screen.
 *
 * "Up by one" is expressed as "after the item two back", because `afterId`
 * names the *new* predecessor and the row being stepped over is the old one.
 * The item at index 1 has no such row, which is what `null` is for.
 */
export function moveTarget(items: Item[], item: Item, direction: MoveDirection): MoveStep | null {
  const bucket = items.filter((i) => sameBucket(i, item));
  const at = bucket.findIndex((i) => i.id === item.id);
  if (at === -1) return null;
  if (direction === 'down') {
    const next = bucket[at + 1];
    return next ? { afterId: next.id } : null;
  }
  if (at === 0) return null;
  return { afterId: at === 1 ? null : bucket[at - 2].id };
}

/**
 * `items` with `itemId` lifted out and dropped back in after `afterId`, or at
 * the top of its bucket for `null` — the array the server would return, guessed
 * client-side for the optimistic write.
 *
 * The array is the server's order, in which a bucket is one contiguous run
 * (`ORDER BY (category IS NULL), LOWER(category), done, position, id` groups
 * every bucket-mate together), so "the top of the bucket" is the index of the
 * first remaining bucket-mate and the run stays contiguous afterwards.
 *
 * Anything it cannot place — an unknown id, an `afterId` that is not on the
 * list — returns the array untouched. The request is still on its way, and its
 * response is the answer; inventing a position the server did not choose would
 * be the second copy of a rule this codebase keeps refusing to write.
 */
export function reorderItems(items: Item[], itemId: number, afterId: number | null): Item[] {
  const moving = items.find((i) => i.id === itemId);
  if (!moving) return items;
  const rest = items.filter((i) => i.id !== itemId);

  let at: number;
  if (afterId === null) {
    at = rest.findIndex((i) => sameBucket(i, moving));
    if (at === -1) return items; // alone in its bucket: already at the top
  } else {
    const after = rest.findIndex((i) => i.id === afterId);
    if (after === -1) return items;
    at = after + 1;
  }
  return [...rest.slice(0, at), moving, ...rest.slice(at)];
}

/**
 * The categories in use on a list, ordered the way the server orders them
 * (`service.categories()` is `GROUP BY category ORDER BY LOWER(category)`).
 *
 * Derived from the loaded items rather than from an endpoint of its own, and
 * deliberately so: a `GET /api/lists/{id}/categories` would carry no
 * information this file does not already hold — categories *are* a projection
 * of items — while adding a second, race-prone source for it, so a stale
 * response could offer a category the item list says nobody uses. It would also
 * cost access-control surface for nothing: `service.categories()` is one of the
 * eight functions that trust their caller, so exposing it means gating it in
 * `_helpers.py` too.
 *
 * Spellings are case-insensitively deduped, first seen winning, because
 * `_canonical_category` makes a list converge on one spelling — two only ever
 * coexist for the frame between an optimistic write and the refetch that
 * corrects it, and offering both as separate choices would make that frame
 * pickable.
 */
export function categoriesOf(items: Item[]): string[] {
  const bySpelling = new Map<string, string>();
  for (const item of items) {
    if (!item.category) continue;
    const key = item.category.toLowerCase();
    if (!bySpelling.has(key)) bySpelling.set(key, item.category);
  }
  return [...bySpelling.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}
